import { useState } from 'react'
import { fbt } from 'fbtee'
import { parseDateOnly, toBcp47 } from '@shared/dates'
import { PREGAME_NOTES_MAX, defaultPregameTime, hasPregameContent, pregameDays, type SavedPlace } from '@shared/pregames'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { AudiencePicker } from './AudiencePicker'
import { PregamePlaceFields } from './PregamePlaceFields'
import { useAudienceSelection } from '../hooks/useAudienceSelection'
import { cancelButtonLabel, saveButtonLabel } from '../lib/commonStrings'
import { todayLocalISO } from '../lib/formatDate'
import { clearPregame, removeSavedPlace, savePregame } from '../hooks/usePregames'
import type { Event, Group, Pregame } from '../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  event: Event
  userId: string
  /** Groups the host is a member of — the audience channels besides Friends. */
  groups: Group[]
  pregame: Pregame | null
  /** The host's places from earlier pre-games; a new pre-game starts from the latest. */
  savedPlaces: SavedPlace[]
  locale: string
}

/** Host, edit or remove the viewer's pre-game for an event, and pick who's invited. */
export function PregameDialog({ open, onOpenChange, pregame, ...form }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" onOpenAutoFocus={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {fbt('Your pre-game', 'Title of the dialog where the viewer sets where and when they meet people before an event')}
          </DialogTitle>
          <DialogDescription>
            {fbt(
              "Where and when you're meeting before the event. Only the people you invite can see it.",
              'Subtitle of the pre-game dialog explaining what it is and who can see it',
            )}
          </DialogDescription>
        </DialogHeader>
        {open && <PregameForm {...form} pregame={pregame} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  )
}

function PregameForm({ event, userId, groups, pregame, savedPlaces, locale, onClose }: Omit<Props, 'open' | 'onOpenChange'> & {
  onClose: () => void
}) {
  const days = pregameDays(event)
  const [day, setDay] = useState(() => {
    if (pregame?.day && days.includes(pregame.day)) return pregame.day
    const today = todayLocalISO()
    return days.includes(today) ? today : days[0] ?? ''
  })
  const [time, setTime] = useState(() => (pregame ? pregame.time ?? '' : defaultPregameTime(event, day || undefined)))
  const [place, setPlace] = useState(() => {
    const from = pregame ? { name: pregame.placeName, address: pregame.address } : savedPlaces[0]
    return { placeName: from?.name ?? '', address: from?.address ?? '' }
  })
  const [notes, setNotes] = useState(pregame?.notes ?? '')
  const audience = useAudienceSelection(groups, pregame ?? undefined)
  const [saving, setSaving] = useState(false)
  const canSave = hasPregameContent({ time, ...place, notes }) && audience.count > 0 && (!days.length || !!day) && !saving

  // runMutation already toasted a failure; the dialog stays open with the draft.
  async function run(action: () => Promise<void>) {
    if (saving) return
    setSaving(true)
    try {
      await action()
      onClose()
    } catch {
      /* toasted */
    } finally {
      setSaving(false)
    }
  }

  const dayLabel = (iso: string) =>
    parseDateOnly(iso).toLocaleDateString(toBcp47(locale), { weekday: 'long', month: 'short', day: 'numeric' })

  return (
    <form
      className="space-y-4"
      onSubmit={e => {
        e.preventDefault()
        if (!canSave) return
        run(() => savePregame({
          eventId: event.id,
          ...(days.length ? { day } : {}),
          time,
          ...place,
          notes,
          ...audience.value,
        }))
      }}
    >
      {days.length > 0 && (
        <div className="space-y-1.5">
          <Label htmlFor="pregame-day">{fbt('Day', 'Label of the festival-day field in the pre-game form')}</Label>
          <Select value={day} onValueChange={setDay}>
            <SelectTrigger id="pregame-day" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {days.map(d => <SelectItem key={d} value={d}>{dayLabel(d)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="pregame-time">{fbt('Time', 'Label of the time field in the pre-game form')}</Label>
        <Input
          id="pregame-time"
          type="time"
          className="appearance-none [&::-webkit-date-and-time-value]:text-left"
          value={time}
          onChange={e => setTime(e.target.value)}
        />
      </div>
      <PregamePlaceFields
        value={place}
        onChange={setPlace}
        savedPlaces={savedPlaces}
        onRemoveSaved={p => removeSavedPlace(userId, savedPlaces, p).catch(() => {}) /* toasted */}
      />
      <div className="space-y-1.5">
        <Label htmlFor="pregame-notes">{fbt('Notes', 'Label of the notes field in the pre-game form')}</Label>
        <Textarea
          id="pregame-notes"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          maxLength={PREGAME_NOTES_MAX}
          rows={3}
          placeholder={fbt("Bring ice, we'll head over at 10", 'Placeholder of the notes field in the pre-game form')}
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {fbt('Invite', 'Label before the audience picker in the pre-game form')}
        </span>
        <AudiencePicker
          groups={groups}
          selection={audience}
          triggerLabel={audience.isEveryone
            ? fbt('All your crew', 'Pre-game audience picker label when every group and friends are invited')
            : fbt(
                fbt.plural('channel', audience.count, { showCount: 'yes', many: 'channels' }),
                'Pre-game audience picker label showing how many groups/friends are invited',
              )}
          header={fbt("Who's invited?", 'Header of the audience picker in the pre-game form')}
          friendsLabel={fbt('Friends', 'Audience option to invite your friends to a pre-game')}
        />
      </div>
      <div className="flex items-center gap-2">
        {pregame && (
          <Button
            type="button"
            variant="ghost"
            className="mr-auto text-destructive"
            disabled={saving}
            onClick={() => run(() => clearPregame(userId, event.id))}
          >
            {fbt('Remove', "Button in the pre-game form that deletes the viewer's pre-game")}
          </Button>
        )}
        <Button type="button" variant="outline" className={pregame ? undefined : 'ml-auto'} onClick={onClose}>
          {cancelButtonLabel()}
        </Button>
        <Button type="submit" disabled={!canSave}>
          {saveButtonLabel()}
        </Button>
      </div>
    </form>
  )
}
